import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import Button from "../../../components/buttons";
import ThemedModal from "../../../components/utilities/ThemedModal";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { BaseModalConfirmation } from "../../../types/DefModalOptions";
import { CloseLoopApplicationType } from "../types";

export type ConfirmExportModalProps = BaseModalConfirmation<never> & {
    proceed?: (closeLoop: boolean) => void;
    totalCount: number;
    excluded: CloseLoopApplicationType[];
};

export const ConfirmExportModal = ({
    proceed,
    cancel,
    dismiss,
    totalCount,
    excluded,
    ...rest
}: Readonly<ConfirmExportModalProps>) => {

    const body = useMemo(() => {
        const excludeCount = excluded.length;
        const letterCount = totalCount - excludeCount;

        if (letterCount === 0)
            return <p>You have chosen to export, but have excluded all relevant applications. Please adjust your selection and try again.</p>;

        if (excludeCount > 0)
            return <>
                You are about to export contact details for {totalCount} application{totalCount !== 1 ? 's' : ''}, and Close Loop letters for {letterCount}.<br />
                No Close Loop letter will be generated for the following application{excludeCount !== 1 ? 's' : ''}:
                <ul>
                    {excluded.map(app => <li>{app.applicationDescription} - {app.masterAccountName}</li>)}
                </ul>
                <p>Do you want to update the status for <strong>all</strong> relevant business sheets?</p>
            </>;

        return <>
            <p>You are about to export Close Loop letters and contact details for {totalCount} application{totalCount !== 1 ? 's' : ''}.</p>
            <p>Do you want to update the status for <strong>all</strong> relevant business sheets?</p>
        </>;
    }, [excluded, totalCount]);

    return (
        <Modal backdrop="static" centered onHide={cancel} size="lg" {...rest}>
            <ThemedModal.Header>
                <Modal.Title>Export</Modal.Title>
            </ThemedModal.Header>
            <Modal.Body>
                {body}
            </Modal.Body>
            <Modal.Footer>
                {totalCount !== excluded.length
                    ? <>
                        <Button variant="success" onClick={() => proceed!(true)}>Yes</Button>
                        <Button variant="warning" onClick={() => proceed!(false)}>No</Button>
                        <Button variant="danger" onClick={cancel}>Cancel Export</Button>
                    </>
                    : <Button variant="light" onClick={cancel}>Close</Button>}
            </Modal.Footer>
        </Modal>
    );
};

export function openConfirmExportModal(totalCount: number, excluded: CloseLoopApplicationType[]): Promise<boolean> {
    return createConfirmation<ConfirmExportModalProps, any>(confirmable(ConfirmExportModal))({
        dialogId: "confirm-export-modal",
        totalCount,
        excluded
    });
}

export default ConfirmExportModal;